import { ReactElement, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Paper } from "@material-ui/core";
import { Row, Col, Button, Typography, Divider, Checkbox } from "antd";
import firebase from "firebase/app";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { getBookingState, getAuthState } from "../../redux/selectors";
import { capitalize } from "../../utils/string";
import Loading from "../../components/Loading";
import { SET_BOOKING_DATE, SET_SHARE_BOOKING, SET_SHARE_BOOKING_DATA, CREATE_BOOKING_REQUEST, SET_LOADING_STATE, RESET_LOADING_STATE } from "../../redux/constants";
import BookingStatus from "./BookingStatus";
import BookingConfirm from "./BookingConfirm";
// import api from "../../redux/api/axiosInstance";
import { firebaseCreateBooking } from "../../redux/api/booking";

const { Paragraph } = Typography;

const timePeriods = [30, 60, 90, 120];

const femaleOnlyDays = [
  "Every Sunday",
  "Every Monday",
  "Every Tuesday",
  "Every Wednesday",
  "Every Thursday",
  "Every Friday",
  "Every Saturday"
]

export function BookingOptions(): ReactElement {
  const { preBranch, preCourts, preBookingDate, preBookingUserId } = useSelector(getBookingState);
  const [courts, setCourts] = useState<any[]>([]);
  const [slots, setSlots] = useState<any[]>([]);
  const [bookingTime, setBookingTime] = useState<any>(null);
  const [bookingDate, setBookingDate] = useState<string>(preBookingDate);
  const [loading, setLoading] = useState<boolean>(true);
  const [duration, setDuration] = useState<number>(90);
  const [courtPlace, setCourtPlace] = useState<string | undefined>(undefined);
  const [courtPlaces, setCourtPlaces] = useState<string[]>([]);
  const [isCompleted, setCompleted] = useState<boolean>(false);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [bookingsForDate, setBookingsForDate] = useState<any[]>([]);
  const [bookingCourt, setBookingCourt] = useState<string>();
  const [enablePrevDate, setEnablePrevDate] = useState<boolean>(true);
  const [enableNextDate, setEnableNextDate] = useState<boolean>(true);
  const [dateInterval, setDateInterval] = useState<number>(1);
  const [femaleOnly, setFemaleOnly] = useState<boolean>(false);
  // only for mobile
  const [showSchedule, setShowSchedule] = useState<boolean>(false);
  const { currentUser } = useSelector(getAuthState);
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const clubData = preBranch?.clubData;

  const grouping = [{ resourceName: "courts" }];
  const resources = [
    {
      fieldName: "courts",
      title: "Courts",
      instances: courts,
      allowMultiple: true,
    },
  ];

  const checkForFemaleOnly = (pSlots: any[]): any[] => {
    const slotsData = [...pSlots];
    if (currentUser?.gender === "female") return pSlots;

    const femaleOnlyData = preBranch?.femaleOnly;
    const dayOfBooking = moment(preBookingDate).day().toString();
    const indexFemaleOnly = femaleOnlyData.findIndex((e: any) => e.day === dayOfBooking);
    if (indexFemaleOnly === -1) return pSlots;

    const fData = femaleOnlyData[indexFemaleOnly];
    const fStartTime = moment(`${moment().format("YYYY-MM-DD")} ${fData.startTime}`);
    let fEndTime = moment(`${moment().format("YYYY-MM-DD")} ${fData.endTime}`);
    if (fStartTime > fEndTime) {
      fEndTime = fEndTime.add(1, "d");
    }
    let nextSlot = fStartTime;
    while (nextSlot < fEndTime) {
      const time = nextSlot.format("h:mm A");
      const index = slotsData.findIndex((e) => e.time === time);
      if (index !== -1) {
        slotsData[index].courts = [];
      }
      nextSlot = nextSlot.add(30, "m");
    }

    return slotsData;
  };

  const getStartEndTime = (): any => {
    let endTime = moment(`${preBookingDate} ${preBranch?.endTime}`);
    let startTime = moment(`${preBookingDate} ${preBranch?.startTime}`);

    if (femaleOnly) {
      const dayOfBookingDate = moment(preBookingDate).day();
      const femaleOnlyInfo = preBranch?.femaleOnly.filter(({ day }: any) => parseInt(day, 10) === dayOfBookingDate);
      // Current get only one femaly only time. if admin sets 2 times more one day. other will be ignored. only first item of filter result will be used.
      if (femaleOnlyInfo.length) {
        endTime = moment(`${preBookingDate} ${femaleOnlyInfo[0].endTime}`);
        startTime = moment(`${preBookingDate} ${femaleOnlyInfo[0].startTime}`);
      }
    }

    if (preBookingDate === moment().format("YYYY-MM-DD") && startTime < moment()) {
      // startTime = moment(`${preBookingDate} ${moment().add(1, "h").format("HH:00")}`);
      // if (startTime.format("YYYY-MM-DD") !== moment().format("YYYY-MM-DD")) {
      //   startTime = endTime;
      // }
    }
    if (startTime > endTime) {
      endTime = endTime.add(1, "d");
      setDateInterval(2);
    }
    return {
      startTime,
      endTime
    };
  }

  const getTimeSlots = (mDuration: number, mPlace: string | undefined, mBookings: any[]) => {
    if (mPlace === "" || mPlace === undefined) {
      setSlots([]);
      return;
    }
    let mSlots: any[] = [];
    const { startTime, endTime } = getStartEndTime();
    const courtsIds: any[] = [];
    preCourts.forEach((court) => {
      if (court.place === mPlace) {
        courtsIds.push(court.courtId);
      }
    });
    let nextSlot = startTime;
    while (nextSlot < endTime) {
      const time = nextSlot.format("HH:mm:ss");
      const timeSlot = { time, timeText: nextSlot.format("h:mm A"), courts: courtsIds, date: nextSlot.format("YYYY-MM-DD") };
      mSlots.push(timeSlot);
      nextSlot = nextSlot.add(30, "m");
    }
    // check for female only.
    mSlots = checkForFemaleOnly(mSlots);

    const spanSlot = mDuration / 30;

    courtsIds.forEach((pCourt) => {
      const pSlots = Array.from(mSlots);
      const pBookings = mBookings.filter((b) => b.courtId === pCourt);
      pBookings.forEach((pb) => {
        const { start, date } = pb;
        const startSlotIndex = pSlots.findIndex((e) => e.time === start && e.date === date);
        const durationSpan = pb.period / 30;
        if (startSlotIndex === -1) return;
        for (let i = 0; i < durationSpan; i += 1) {
          if (i + startSlotIndex < mSlots.length) {
            mSlots[i + startSlotIndex].courts = mSlots[i + startSlotIndex].courts.filter((e: string) => e !== pCourt);
          }
        }
      });
      mSlots.forEach((ps, index) => {
        if (ps.courts.indexOf(pCourt) === -1) {
          for (let i = 1 - spanSlot; i < 0; i += 1) {
            if (i + index > -1 && i + index < mSlots.length) {
              mSlots[i + index].courts = mSlots[i + index].courts.filter((e: string) => e !== pCourt);
            }
          }
        }
      });

    });
    // If duration is over 30 mins, it will disable last n(same as durationSpan - 1) slots
    const disableLastSlotsCount = mSlots.length > spanSlot ? spanSlot : mSlots.length;
    for (let i = 1; i < disableLastSlotsCount; i += 1) {
      mSlots[mSlots.length - i].courts = [];
    }
    const currentTime = moment().format("H:mm:ss");
    const currentDate = moment().format("YYYY-MM-DD");
    const finalSlots = mSlots.filter((s) => {
      if (s.date === currentDate)
        return s.time > currentTime && s.courts.length > 0;
      return s.courts.length > 0;
    })
    setSlots(finalSlots);
  };

  const getAllBookingForDate = async () => {
    const { startTime, endTime } = getStartEndTime();
    const filterDates = [startTime.format("YYYY-MM-DD"), endTime.format("YYYY-MM-DD")];
    const bookSnapshots = await firebase
      .firestore()
      .collection("bookings")
      .where("branchId", "==", preBranch?.firestoreRef)
      .where("date", "in", filterDates)
      .where("status", "==", "booked")
      .get();
    const mBookingsForDate: any[] = [];
    const mBookings = bookSnapshots.docs.map((book, index) => {
      const bookDataFirebase = book.data();
      mBookingsForDate.push(bookDataFirebase);
      const bookingStartDate = moment(`${bookDataFirebase.date} ${bookDataFirebase.start}`);
      const bookData = {
        id: index,
        title: "",
        courts: [bookDataFirebase.courtId],
        startDate: bookingStartDate.toDate(),
        endDate: bookingStartDate.add(bookDataFirebase.period, "m").toDate(),
      };
      return bookData;
    });
    setSchedule(mBookings);
    setBookingsForDate(mBookingsForDate);
    return mBookingsForDate;
  }

  useEffect(() => {
    const mCourts: any[] = [];
    const mPlaces: any[] = [];
    preCourts.forEach((court) => {
      mCourts.push({
        text: `${court.name}(${capitalize(court.place)})`,
        id: court.courtId,
        color: "blue",
      });
      if (mPlaces.indexOf(court.place) === -1) {
        mPlaces.push(court.place);
      }
    });
    setCourts(mCourts);
    setCourtPlaces(mPlaces);
    setCourtPlace(mPlaces[0]);
    getAllBookingForDate().then((bookings) => {
      getTimeSlots(duration, mPlaces[0], bookings);
      setTimeout(() => {
        setLoading(false);
      }, 500);
    });

  }, [preBookingDate]);

  useEffect(() => {
    const daysRange = clubData?.bookingDatesRange;
    if (moment(preBookingDate).subtract(1, "d").format("YYYY-MM-DD") < moment().format("YYYY-MM-DD")) {
      setEnablePrevDate(false);
    }
    const endOfBookingDate = moment().add(daysRange, "d");

    if (moment(preBookingDate).format("YYYY-MM-DD") < moment().add(1, "d").format("YYYY-MM-DD")) {
      setEnablePrevDate(false);
    } else {
      setEnablePrevDate(true);
    }

    if (moment(preBookingDate).add(1, "d").format("YYYY-MM-DD") > endOfBookingDate.format("YYYY-MM-DD")) {
      setEnableNextDate(false);
    } else {
      setEnableNextDate(true);
    }


    // const today = moment().date();
    // const currentMonth = moment().month();
    // const nextMonth = currentMonth + 1;
    // const nextDate = moment(preBookingDate).add(1, "d");
    // if (today < 20 && nextDate.add(1, "d").month() > moment().month()) {
    //   setEnableNextDate(false);
    // } else if(today > 19 && nextDate.month() > nextMonth ){
    //   setEnableNextDate(false);
    // } else {
    //   setEnableNextDate(true);
    // }
  }, [])

  const selectDuration = (value: number) => {
    setLoading(true);
    setDuration(value);
    setBookingTime(null);
    getTimeSlots(value, courtPlace, bookingsForDate);
    setLoading(false);
  };

  const onPressConfirm = () => {
    setCompleted(true);
  };

  const onSelectCourtPlace = (value: string) => {
    setLoading(true);
    setCourtPlace(value);
    if (duration) {
      getTimeSlots(duration, value, bookingsForDate);
    }
    setLoading(false);
  };

  const createBook = (additionalInfo: string, paymentMethod: string, transactionId: string) => {
    const newBooking = {
      bookingId: uuidv4(),
      userId: preBookingUserId !== "" ? preBookingUserId : currentUser?.id,
      branchId: preBranch?.firestoreRef,
      courtId: bookingCourt,
      date: bookingDate,
      period: duration,
      start: bookingTime,
      ball: false,
      club: preBranch?.clubData.firestoreRef,
      price: preBranch?.clubData.price[duration],
      currency: preBranch?.clubData.currency,
      additionalInfo,
      paymentMethod,
      transactionId,
    };
    dispatch({ type: SET_LOADING_STATE });
    firebaseCreateBooking({ data: newBooking }).
      then((res: any) => {
        setCompleted(false);
        dispatch({ type: RESET_LOADING_STATE });
        history.push("/dashboard");
      })
      .catch((e: any) => {
        dispatch({ type: RESET_LOADING_STATE });
      })

  }

  const completeBooking = async (additionalInfo: string, paymentMethod: string, transactionId = "") => {
    if (currentUser) {
      createBook(additionalInfo, paymentMethod, transactionId);
    } else {
      dispatch({ type: SET_SHARE_BOOKING });
      dispatch({
        type: SET_SHARE_BOOKING_DATA,
        payload: {
          bookingTime,
          bookingDuration: duration,
          bookingCourt,
          additionalInfo,
        },
      });
      history.push("/login");
    }
  };

  const backBooking = () => {
    setCompleted(false);
  };

  const onPrevDate = () => {
    setLoading(true);
    setSlots([]);
    const dateSpan = 1;
    let prevDate = moment(preBookingDate).subtract(dateSpan, "d");

    let isValidDate = !femaleOnly;
    while (!isValidDate) {
      const dayOfPrevDate = prevDate.day();
      if (preBranch?.femaleOnly.find((f: any) => parseInt(f.day, 10) === dayOfPrevDate)) {
        isValidDate = true;
      } else {
        prevDate = prevDate.subtract(dateSpan, "d");
      }
    }
    const dateAsString = prevDate.format("YYYY-MM-DD");
    dispatch({
      type: SET_BOOKING_DATE,
      payload: { date: dateAsString },
    });
    if (femaleOnly) {
      let nextAvaliableDate = prevDate.subtract(1, "d");
      isValidDate = false;
      while (isValidDate) {
        const dayOfNextPrevDate = nextAvaliableDate.day();
        if (preBranch?.femaleOnly.find((f: any) => parseInt(f.day, 10) === dayOfNextPrevDate)) {
          isValidDate = true;
        } else {
          nextAvaliableDate = nextAvaliableDate.subtract(1, "d");
        }
      }
      if (nextAvaliableDate.format("YYYY-MM-DD") < moment().format("YYYY-MM-DD")) {
        setEnablePrevDate(false);
      } else {
        setEnablePrevDate(true);
      }
    } else if (prevDate.format("YYYY-MM-DD") < moment().add(1, "d").format("YYYY-MM-DD")) {
      setEnablePrevDate(false);
    } else {
      setEnablePrevDate(true);
    }
    setEnableNextDate(true);
    setBookingTime(null);
  };

  const onNextDate = () => {
    setLoading(true);
    setSlots([]);
    const dateSpan = 1;
    const today = moment().date();
    const currentMonth = moment().month();
    const nextMonth = currentMonth + 1;
    let nextDate = moment(preBookingDate).add(dateSpan, "d");
    let isValidDate = !femaleOnly;
    while (!isValidDate) {
      const dayOfNextDate = nextDate.day();
      if (preBranch?.femaleOnly.find((f: any) => parseInt(f.day, 10) === dayOfNextDate)) {
        isValidDate = true;
      } else {
        nextDate = nextDate.add(dateSpan, "d");
      }
    }

    const dateAsString = nextDate.format("YYYY-MM-DD");
    dispatch({
      type: SET_BOOKING_DATE,
      payload: { date: dateAsString },
    });

    const daysRange = clubData?.bookingDatesRange;
    const endOfBookingDate = moment().add(daysRange, "d");

    if (nextDate.add(1, "d").format("YYYY-MM-DD") > endOfBookingDate.format("YYYY-MM-DD")) {
      setEnableNextDate(false);
    } else {
      setEnableNextDate(true);
    }

    setEnablePrevDate(true);
    setBookingTime("");
  };

  const selectBookingTime = (slot: any) => {
    setBookingDate(slot.date);
    setBookingTime(slot.time);
    setBookingCourt(slot.courts[0]);
  }
  const onChangeFemaleOnly = (e: any) => {
    setFemaleOnly(e.target.checked);
    setDateInterval(1);
  }

  useEffect(() => {
    // reset current state
    getTimeSlots(duration, courtPlace, bookingsForDate);
  }, [femaleOnly])

  return (
    <Row className="booking-content-wrapper">
      <Col sm={24} md={{ span: isCompleted && clubData?.payOverCount ? 8 : 24, offset: isCompleted && clubData?.payOverCount ? 8 : 0 }}>
        <Paper className="booking-card" style={{ display: isCompleted ? "none" : "flex" }}>
          <Row>
            <div className="booking-show-schedule-button-container">
              <Button onClick={() => setShowSchedule(!showSchedule)} className={`hideDesktop ${isCompleted ? "hide" : "show"}`} type="primary">
                {showSchedule ? t("booking.book") : t("booking.status")}
              </Button>
            </div>
            <Col sm={24} md={8} className={`booking-time-slots-container ${showSchedule ? "hide" : "show"}`}>
              <div className="booking-header-title text-label">
                <span className="text-uppercase">{t("booking.select_date_time")}</span>
                <p style={{ fontSize: 14, marginBottom: 0, fontWeight: 400 }}>{preBranch?.clubData.name}, {preBranch?.location}</p>
              </div>
              <p className="text-uppercase text-label">{t("booking.select_duration")} ({t("minutes")})</p>
              <Row>
                {timePeriods.map((p) => (
                  <Col span={6} className="time-slot-button-container" key={p}>
                    <Button className={`time-slot-button ${duration === p ? "active" : ""}`} onClick={() => selectDuration(p)}>
                      {p}
                      <p style={{ fontSize: 12 }}>{preBranch?.clubData.price[p]} {preBranch?.clubData.currency}</p>
                    </Button>
                  </Col>
                ))}
              </Row>
              {courtPlaces.length === 2 &&
                <Row>
                  <p className="text-uppercase text-label" style={{ marginTop: 20 }}>{t("court.court_place")}</p>
                  <Col span={24} className="booking-court-select-container">
                    <Button className={courtPlace === "indoor" ? "booking-court-select-button-active" : "booking-court-select-button"} disabled={courtPlaces.findIndex((c) => c === "indoor") === -1} onClick={() => onSelectCourtPlace("indoor")}>
                      {t("court.indoor")}
                    </Button>
                    <Button className={courtPlace === "outdoor" ? "booking-court-select-button-active" : "booking-court-select-button"} disabled={courtPlaces.findIndex((c) => c === "outdoor") === -1} onClick={() => onSelectCourtPlace("outdoor")}>
                      {t("court.outdoor")}
                    </Button>
                  </Col>
                </Row>
              }
              {preBranch?.femaleOnly.length > 0 &&
                <Row style={{ marginTop: 20 }}>
                  <Col span={24}>
                    <Checkbox onChange={onChangeFemaleOnly}>Female Only</Checkbox>
                  </Col>
                  {femaleOnly && <ul className="female_only_list">
                    {preBranch?.femaleOnly.map(({ day, startTime, endTime }: any) => (
                      <li className="female_only_list_item">{`${femaleOnlyDays[parseInt(day, 10)]}, ${moment(startTime, "HH:mm").format("h:mm A")} ~ ${moment(endTime, "HH:mm").format("h:mm A")}`}</li>
                    ))}
                  </ul>}
                </Row>
              }
              <Row style={{ marginTop: 20 }}>
                <Col span={4}>
                  <Button className="booking-date-arrow-icon" style={{ float: "right" }} onClick={onPrevDate} disabled={!enablePrevDate || loading}>
                    <ArrowLeftOutlined />
                  </Button>
                </Col>
                <Col span={16} style={{ textAlign: "center" }}>
                  <div>{moment(preBookingDate).format("dddd")}</div>
                  <div>{moment(preBookingDate).format("MMMM D, YYYY")}</div>
                </Col>
                <Col span={4}>
                  <Button
                    className="booking-date-arrow-icon"
                    style={{ float: "left" }}
                    onClick={onNextDate}
                    disabled={!enableNextDate || loading}
                  >
                    <ArrowRightOutlined />
                  </Button>
                </Col>
              </Row>
              <Row style={{ marginTop: 20 }}>
                {slots.map((slot, index) => (
                  <>
                    <Col xs={12} key={slot.time} className="booking-time-slot-button-container">
                      {bookingTime !== slot.time && (
                        <Button className="booking-time-slot-button" onClick={() => selectBookingTime(slot)} disabled={slot.courts.length === 0}>
                          {slot.timeText}
                        </Button>
                      )}
                      {bookingTime === slot.time && (
                        <Button className="booking-time-slot-confirm" type="primary" block onClick={onPressConfirm}>
                          {t("booking.confirm")}
                        </Button>
                      )}
                    </Col>
                    {(slots[index + 1] && slot.date !== slots[index + 1].date) && (
                      <Col span={24}>
                        <Divider style={{ borderColor: '#7cb305', fontWeight: 400, fontSize: 14 }} dashed>
                          {moment(slots[index + 1].date).format("MMM DD, dddd")}
                        </Divider>
                      </Col>
                    )}
                  </>
                ))}
                {slots.length === 0 && (
                  <Paragraph><pre>{t("booking.empty_slots")}</pre></Paragraph>
                )}
              </Row>
            </Col>
            <BookingStatus
              showSchedule={showSchedule}
              loading={loading}
              schedule={schedule}
              grouping={grouping}
              dateInterval={dateInterval}
              resources={resources}
            />
          </Row>
        </Paper>
        {isCompleted && (
          <BookingConfirm
            bookingDate={bookingDate}
            bookingTime={bookingTime}
            duration={duration}
            loading={loading}
            onComplete={completeBooking}
            onBack={backBooking}
            court={preCourts.find(pc => pc.courtId === bookingCourt)}
          />
        )}
      </Col>
      {loading && <Loading />}
    </Row>
  );
}
